<template>
  <div class="detail-page">
    <!-- 헤더 -->
    <header class="detail-header">
      <button class="header-btn" @click="goBack">
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      <h1 class="header-title">{{ property.name || '현장 상세' }}</h1>
      <div class="header-actions">
        <button class="header-btn" @click="shareProperty">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
          </svg>
        </button>
        <button class="header-btn" @click="handleToggleFavorite">
          <svg
            class="w-6 h-6"
            :class="isFavorited ? 'text-red-500' : ''"
            :fill="isFavorited ? 'currentColor' : 'none'"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
          </svg>
        </button>
      </div>
    </header>

    <!-- 로딩 -->
    <div v-if="loading" class="state-box">
      <div class="spinner"></div>
      <p>불러오는 중...</p>
    </div>

    <!-- 에러 -->
    <div v-else-if="error" class="state-box">
      <p class="text-red-500">{{ error }}</p>
      <router-link to="/" class="mt-4 text-blue-600">홈으로</router-link>
    </div>

    <!-- 컨텐츠 -->
    <div v-else class="content">
      <!-- 요약 정보 (썸네일 위) -->
      <div class="summary-bar">
        <p class="summary-address">{{ property.address }}</p>
        <div class="summary-info">
          <span v-if="property.propertyType">{{ getPropertyTypeLabel(property.propertyType) }}</span>
          <span v-if="property.totalUnits" class="divider">|</span>
          <span v-if="property.totalUnits">{{ property.totalUnits?.toLocaleString() }}세대</span>
          <template v-if="property.themes && property.themes.length">
            <span class="divider">|</span>
            <span v-for="(t, idx) in property.themes.slice(0, 2)" :key="t">
              {{ getThemeLabel(t) }}<span v-if="idx < Math.min(property.themes.length, 2) - 1">, </span>
            </span>
            <span v-if="property.themes.length > 2">외 {{ property.themes.length - 2 }}개</span>
          </template>
        </div>
      </div>

      <!-- 이미지 -->
      <div class="image-area">
        <img v-if="property.thumbnailUrl" :src="property.thumbnailUrl" :alt="property.name" />
        <div v-else class="no-image">
          <svg class="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
        </div>
        <div class="badges">
          <span :class="['badge', `badge-${property.status}`]">{{ getStatusLabel(property.status) }}</span>
        </div>
      </div>

      <!-- 기본 정보 (현장명 + 가격 범위) -->
      <div class="section">
        <h1 class="name">{{ property.name }}</h1>
        <p class="price-range" v-if="priceRange">
          <span class="range-value">{{ priceRange }}</span>
        </p>
      </div>

      <!-- 가격 정보 -->
      <div class="section price-section">
        <div class="price-row">
          <span class="label">초기자금</span>
          <span class="value primary">{{ property.initialFund || '-' }}</span>
        </div>
        <div class="price-row">
          <span class="label">중도금</span>
          <span class="value">{{ property.middlePayment || '-' }}</span>
        </div>
        <div class="price-row">
          <span class="label">입주예정</span>
          <span class="value">{{ formatMoveInDate(property.moveInDate) }}</span>
        </div>
      </div>

      <!-- 건물 정보 -->
      <div class="section">
        <h2 class="section-title">건물 정보</h2>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">총 세대</span>
            <span class="info-value">{{ property.totalUnits?.toLocaleString() || '-' }}세대</span>
          </div>
          <div class="info-item">
            <span class="info-label">동 수</span>
            <span class="info-value">{{ property.totalBuildings || '-' }}개동</span>
          </div>
          <div class="info-item">
            <span class="info-label">최고층</span>
            <span class="info-value">{{ property.maxFloor || '-' }}층</span>
          </div>
          <div class="info-item">
            <span class="info-label">주차</span>
            <span class="info-value">세대당 {{ property.parkingPerUnit || '-' }}대</span>
          </div>
          <div class="info-item">
            <span class="info-label">용적률</span>
            <span class="info-value">{{ property.floorAreaRatio || '-' }}%</span>
          </div>
          <div class="info-item">
            <span class="info-label">건폐율</span>
            <span class="info-value">{{ property.buildingCoverageRatio || '-' }}%</span>
          </div>
          <div class="info-item">
            <span class="info-label">난방</span>
            <span class="info-value">{{ getHeatingLabel(property.heatingType) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">가스</span>
            <span class="info-value">{{ getGasLabel(property.gasType) }}</span>
          </div>
        </div>
      </div>

      <!-- 평형/타입 -->
      <div v-if="property.types && property.types.length" class="section type-section">
        <h2 class="section-title">평형/타입</h2>

        <!-- 데스크탑: 탭 버튼 (가로 일렬) -->
        <div class="type-tabs desktop-only">
          <button
            v-for="(type, idx) in property.types"
            :key="idx"
            class="type-tab"
            :class="{ active: selectedTypeIdx === idx }"
            @click="selectedTypeIdx = idx"
          >
            {{ type.name }}
          </button>
        </div>

        <!-- 데스크탑: 선택된 타입 상세 -->
        <div v-if="property.types[selectedTypeIdx]" class="type-detail-card desktop-only">
          <div class="type-detail-header">
            <div class="type-detail-left">
              <span class="type-detail-name">{{ property.types[selectedTypeIdx].name }}</span>
              <span class="type-detail-pyeong">{{ property.types[selectedTypeIdx].pyeong }}평</span>
            </div>
            <span class="type-detail-price">{{ formatPrice(property.types[selectedTypeIdx].price) }}</span>
          </div>

          <div class="type-detail-info">
            <div class="info-item">
              <span class="info-label">공급면적</span>
              <span class="info-value">{{ property.types[selectedTypeIdx].supplyArea }}㎡</span>
            </div>
            <div v-if="property.types[selectedTypeIdx].buildings?.length" class="info-item">
              <span class="info-label">해당 동</span>
              <span class="info-value">{{ property.types[selectedTypeIdx].buildings.join(', ') }}동</span>
            </div>
          </div>

          <img
            v-if="property.types[selectedTypeIdx].imageUrl"
            :src="property.types[selectedTypeIdx].imageUrl"
            :alt="`${property.types[selectedTypeIdx].name} 평면도`"
            class="type-floorplan"
          />
        </div>

        <!-- 모바일: 슬라이드 카드 -->
        <div class="type-slider mobile-only">
          <div
            class="type-slider-track"
            :style="{ transform: `translateX(-${selectedTypeIdx * 100}%)` }"
            @touchstart="onTypeSwipeStart"
            @touchmove="onTypeSwipeMove"
            @touchend="onTypeSwipeEnd"
          >
            <div
              v-for="(type, idx) in property.types"
              :key="idx"
              class="type-slide"
            >
              <div class="type-slide-card">
                <div class="type-slide-header">
                  <div class="type-slide-left">
                    <span class="type-slide-name">{{ type.name }}</span>
                    <span class="type-slide-pyeong">{{ type.pyeong }}평</span>
                  </div>
                  <span class="type-slide-price">{{ formatPrice(type.price) }}</span>
                </div>

                <div class="type-slide-info">
                  <div class="slide-info-item">
                    <span class="slide-info-label">공급면적</span>
                    <span class="slide-info-value">{{ type.supplyArea }}㎡</span>
                  </div>
                  <div v-if="type.buildings?.length" class="slide-info-item">
                    <span class="slide-info-label">해당 동</span>
                    <span class="slide-info-value">{{ type.buildings.join(', ') }}동</span>
                  </div>
                </div>

                <img
                  v-if="type.imageUrl"
                  :src="type.imageUrl"
                  :alt="`${type.name} 평면도`"
                  class="type-slide-floorplan"
                />
              </div>
            </div>
          </div>

          <!-- 슬라이드 인디케이터 -->
          <div class="type-slider-dots">
            <button
              v-for="(type, idx) in property.types"
              :key="idx"
              class="slider-dot"
              :class="{ active: selectedTypeIdx === idx }"
              @click="selectedTypeIdx = idx"
            ></button>
          </div>
        </div>
      </div>

      <!-- 위치 지도 -->
      <div class="section">
        <h2 class="section-title">위치</h2>
        <div class="map-box" ref="mapContainer"></div>
        <p class="map-addr">{{ property.address }}</p>
      </div>

      <!-- 주변 편의시설 (미리보기 5개) -->
      <div v-if="hasConvenienceFacilities" class="section convenience-section">
        <div class="convenience-header">
          <h2 class="convenience-title">주변 편의시설</h2>
        </div>
        <div class="convenience-list">
          <div v-for="item in getConveniencePreview" :key="item.name" class="convenience-item">
            <div class="convenience-item-left">
              <svg class="convenience-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" :d="getIconPath(getFacilityType(item, 'convenience'))"/>
              </svg>
              <span class="convenience-name">{{ item.name }}</span>
            </div>
            <span class="convenience-distance">{{ formatDistance(item.distance) }}</span>
          </div>
        </div>
        <!-- 하단 자세히보기 버튼 -->
        <button class="detail-view-btn" @click="openFacilityDetail('convenience')">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
          </svg>
          주변 상권 자세히보기
        </button>
      </div>

      <!-- 주변 대중교통 -->
      <div v-if="hasTransportFacilities" class="section transport-section">
        <h2 class="transport-title">주변 대중교통</h2>

        <!-- 지하철역 -->
        <div v-if="property.facilities?.transport?.subway?.length" class="transport-category">
          <div class="transport-cat-header">
            <svg class="transport-cat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z"/>
            </svg>
            <span>지하철역</span>
          </div>
          <div class="subway-list">
            <div v-for="item in property.facilities.transport.subway.slice(0, 5)" :key="'subway-'+item.name" class="subway-item">
              <div class="subway-item-left">
                <span class="subway-line-badge" :style="{ background: getSubwayLineColor(item.name) }">
                  {{ getSubwayLineNumber(item.name) }}
                </span>
                <span class="subway-name">{{ item.name }}</span>
              </div>
              <span class="subway-distance">{{ formatDistance(item.distance) }}</span>
            </div>
          </div>
        </div>

        <!-- 고속철도/일반철도 -->
        <div v-if="property.facilities?.transport?.rail?.length" class="transport-category">
          <div class="transport-cat-header">
            <svg class="transport-cat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            <span>고속철도/일반철도</span>
          </div>
          <div class="transport-list">
            <div v-for="item in property.facilities.transport.rail.slice(0, 3)" :key="'rail-'+item.name" class="transport-item">
              <span class="transport-name">{{ item.name }}</span>
              <span class="transport-distance">{{ formatDistance(item.distance) }}</span>
            </div>
          </div>
        </div>

        <!-- 버스정류장 -->
        <div v-if="property.facilities?.transport?.bus?.length" class="transport-category">
          <div class="transport-cat-header">
            <svg class="transport-cat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7h8M8 11h8m-8 4h4m-7 5h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2zm2-2v2m8-2v2"/>
            </svg>
            <span>버스정류장</span>
          </div>
          <div class="transport-list">
            <div v-for="item in property.facilities.transport.bus.slice(0, 3)" :key="'bus-'+item.name" class="transport-item">
              <span class="transport-name">{{ item.name }}</span>
              <span class="transport-distance">{{ formatDistance(item.distance) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 어린이집/유치원 섹션 -->
      <div v-if="hasPreschoolFacilities" class="section preschool-section">
        <h2 class="preschool-title">어린이집/유치원</h2>

        <!-- 탭 버튼 -->
        <div class="preschool-tabs">
          <button
            class="preschool-tab"
            :class="{ active: preschoolTab === 'daycare' }"
            @click="preschoolTab = 'daycare'"
          >
            어린이집
          </button>
          <button
            class="preschool-tab"
            :class="{ active: preschoolTab === 'kindergarten' }"
            @click="preschoolTab = 'kindergarten'"
          >
            유치원
          </button>
        </div>

        <!-- 어린이집 -->
        <div v-if="preschoolTab === 'daycare'" class="preschool-content">
          <div v-if="property.facilities?.education?.daycare?.length" class="preschool-list">
            <div v-for="item in property.facilities.education.daycare.slice(0, 10)" :key="'daycare-'+item.name" class="preschool-item">
              <span class="preschool-name">{{ item.name }}</span>
              <span class="preschool-distance">{{ formatDistance(item.distance) }}</span>
            </div>
          </div>
          <p v-else class="preschool-empty">주변에 어린이집 정보가 없습니다</p>
        </div>

        <!-- 유치원 -->
        <div v-if="preschoolTab === 'kindergarten'" class="preschool-content">
          <div v-if="property.facilities?.education?.kindergarten?.length" class="preschool-list">
            <div v-for="item in property.facilities.education.kindergarten.slice(0, 10)" :key="'kinder-'+item.name" class="preschool-item">
              <span class="preschool-name">{{ item.name }}</span>
              <span class="preschool-distance">{{ formatDistance(item.distance) }}</span>
            </div>
          </div>
          <p v-else class="preschool-empty">주변에 유치원 정보가 없습니다</p>
        </div>
      </div>

      <!-- 초중고 학군 섹션 (도보 시간 색상 표시) -->
      <div v-if="hasSchoolFacilities" class="section school-section">
        <h2 class="school-title">초/중/고등학교</h2>

        <!-- 도보 시간 범례 -->
        <div class="school-legend">
          <span class="legend-item"><span class="legend-dot walk-5"></span>5분 이내</span>
          <span class="legend-item"><span class="legend-dot walk-10"></span>10분</span>
          <span class="legend-item"><span class="legend-dot walk-15"></span>15분</span>
          <span class="legend-item"><span class="legend-dot walk-20"></span>20분</span>
          <span class="legend-item"><span class="legend-dot walk-over"></span>20분+</span>
        </div>

        <!-- 탭 버튼 -->
        <div class="school-tabs">
          <button
            class="school-tab"
            :class="{ active: schoolTab === 'elementary' }"
            @click="schoolTab = 'elementary'"
          >
            초등학교
          </button>
          <button
            class="school-tab"
            :class="{ active: schoolTab === 'middle' }"
            @click="schoolTab = 'middle'"
          >
            중학교
          </button>
          <button
            class="school-tab"
            :class="{ active: schoolTab === 'high' }"
            @click="schoolTab = 'high'"
          >
            고등학교
          </button>
        </div>

        <!-- 초등학교 -->
        <div v-if="schoolTab === 'elementary'" class="school-content">
          <div v-if="property.facilities?.education?.elementary?.length" class="school-list">
            <div v-for="item in property.facilities.education.elementary.slice(0, 10)" :key="'elem-'+item.name" class="school-item">
              <div class="school-item-left">
                <span class="walk-badge" :class="getWalkClass(item.distance)">{{ getWalkMinutes(item.distance) }}분</span>
                <span class="school-name">{{ item.name }}</span>
              </div>
              <span class="school-distance">{{ formatDistance(item.distance) }}</span>
            </div>
          </div>
          <p v-else class="school-empty">주변에 초등학교 정보가 없습니다</p>
        </div>

        <!-- 중학교 -->
        <div v-if="schoolTab === 'middle'" class="school-content">
          <div v-if="property.facilities?.education?.middle?.length" class="school-list">
            <div v-for="item in property.facilities.education.middle.slice(0, 10)" :key="'middle-'+item.name" class="school-item">
              <div class="school-item-left">
                <span class="walk-badge" :class="getWalkClass(item.distance)">{{ getWalkMinutes(item.distance) }}분</span>
                <span class="school-name">{{ item.name }}</span>
              </div>
              <span class="school-distance">{{ formatDistance(item.distance) }}</span>
            </div>
          </div>
          <p v-else class="school-empty">주변에 중학교 정보가 없습니다</p>
        </div>

        <!-- 고등학교 -->
        <div v-if="schoolTab === 'high'" class="school-content">
          <div v-if="property.facilities?.education?.high?.length" class="school-list">
            <div v-for="item in property.facilities.education.high.slice(0, 10)" :key="'high-'+item.name" class="school-item">
              <div class="school-item-left">
                <span class="walk-badge" :class="getWalkClass(item.distance)">{{ getWalkMinutes(item.distance) }}분</span>
                <span class="school-name">{{ item.name }}</span>
              </div>
              <span class="school-distance">{{ formatDistance(item.distance) }}</span>
            </div>
          </div>
          <p v-else class="school-empty">주변에 고등학교 정보가 없습니다</p>
        </div>
      </div>

      <!-- 이런 현장 어때요? (추천 섹션) -->
      <div class="section recommend-section">
        <h2 class="section-title">이런 현장 어때요?</h2>
        <div class="recommend-cards">
          <!-- AD 카드 (인앱 광고) -->
          <div
            v-if="recommendAdBanner"
            class="recommend-card ad-card"
            @click="handleAdClick(recommendAdBanner)"
          >
            <div class="ad-badge">AD</div>
            <img :src="recommendAdBanner.imageUrl" :alt="recommendAdBanner.title" class="recommend-img" />
            <div class="recommend-info">
              <p class="recommend-name">{{ recommendAdBanner.propertyName }}</p>
              <p class="recommend-sub">{{ recommendAdBanner.title }}</p>
            </div>
          </div>

          <!-- 유사 현장 카드 (최대 2개, AD 없으면 3개) -->
          <router-link
            v-for="rec in similarProperties"
            :key="rec.id"
            :to="`/properties/${rec.id}`"
            class="recommend-card"
          >
            <img v-if="rec.thumbnailUrl" :src="rec.thumbnailUrl" :alt="rec.name" class="recommend-img" />
            <div v-else class="recommend-img recommend-img-empty">
              <svg class="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
            <div class="recommend-info">
              <p class="recommend-name">{{ rec.name }}</p>
              <p class="recommend-sub">{{ rec.address?.split(' ').slice(0, 2).join(' ') }}</p>
            </div>
          </router-link>
        </div>
      </div>

      <!-- 댓글 섹션 -->
      <div class="section comment-section">
        <div class="comment-header">
          <h2 class="section-title">
            <svg class="w-5 h-5 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
            댓글
            <span class="comment-count">{{ commentCount }}</span>
          </h2>
        </div>

        <!-- 댓글 입력 -->
        <div class="comment-input-box">
          <textarea
            v-model="newComment"
            :placeholder="isLoggedIn ? '의견을 남겨주세요' : '로그인 후 작성 가능'"
            :disabled="!isLoggedIn"
            class="comment-textarea"
            rows="1"
          ></textarea>
          <button
            @click="submitComment"
            :disabled="!newComment.trim() || commentLoading || !isLoggedIn"
            class="comment-submit-btn"
          >
            {{ commentLoading ? '...' : '등록' }}
          </button>
        </div>

        <!-- 댓글 목록 -->
        <div class="comment-list">
          <div v-if="comments.length === 0" class="no-comments">
            <p>아직 댓글이 없습니다. 첫 댓글을 남겨보세요!</p>
          </div>

          <div v-for="comment in comments" :key="comment.id" class="comment-item">
            <!-- 댓글 본문 -->
            <div class="comment-main">
              <div class="comment-meta">
                <span class="comment-author">{{ comment.maskedEmail }}</span>
                <span class="comment-time">{{ formatTimeAgo(comment.createdAt) }}</span>
              </div>
              <p class="comment-content">{{ comment.content }}</p>
              <div class="comment-actions">
                <button @click="openReplyInput(comment.id)" class="comment-action-btn">
                  답글
                </button>
                <button
                  v-if="comment.replyCount > 0"
                  @click="toggleReplies(comment.id)"
                  class="comment-action-btn reply-toggle"
                >
                  {{ expandedReplies[comment.id] ? '답글 숨기기' : `답글 ${comment.replyCount}개 보기` }}
                </button>
                <button
                  v-if="isLoggedIn && user?.uid === comment.userId"
                  @click="handleDeleteComment(comment.id)"
                  class="comment-action-btn delete"
                >
                  삭제
                </button>
              </div>
            </div>

            <!-- 답글 입력 -->
            <div v-if="replyingTo === comment.id" class="reply-input-box">
              <textarea
                v-model="replyContent"
                placeholder="답글을 입력하세요"
                class="reply-textarea"
                rows="2"
              ></textarea>
              <div class="reply-input-actions">
                <button @click="cancelReply" class="reply-cancel-btn">취소</button>
                <button
                  @click="submitReply(comment.id)"
                  :disabled="!replyContent.trim() || commentLoading"
                  class="reply-submit-btn"
                >
                  {{ commentLoading ? '등록 중...' : '답글 등록' }}
                </button>
              </div>
            </div>

            <!-- 대댓글 목록 -->
            <div v-if="expandedReplies[comment.id]" class="reply-list">
              <div v-if="loadingReplies[comment.id]" class="reply-loading">
                답글 불러오는 중...
              </div>
              <div v-else v-for="reply in expandedReplies[comment.id]" :key="reply.id" class="reply-item">
                <div class="reply-meta">
                  <span class="reply-author">{{ reply.maskedEmail }}</span>
                  <span class="reply-time">{{ formatTimeAgo(reply.createdAt) }}</span>
                </div>
                <p class="reply-content">{{ reply.content }}</p>
                <div class="reply-actions">
                  <button
                    v-if="isLoggedIn && user?.uid === reply.userId"
                    @click="handleDeleteComment(reply.id, comment.id)"
                    class="comment-action-btn delete"
                  >
                    삭제
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 상권 상세 바텀시트 -->
      <div v-if="showFacilityDetail" class="area-sheet-overlay" @click.self="closeFacilityDetail">
        <div class="area-sheet">
          <!-- 핸들 -->
          <div class="area-sheet-handle">
            <div class="handle-bar"></div>
          </div>

          <!-- 헤더 -->
          <div class="area-sheet-header">
            <div class="area-sheet-title">
              <span class="area-property-name">{{ property.name }}</span>
              <span class="area-title-sub">상권 모음</span>
            </div>
            <button class="area-close-btn" @click="closeFacilityDetail">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- 지도 (3km 원 표시) -->
          <div class="area-sheet-map" ref="facilityMapContainer"></div>

          <!-- 시설 목록 (상권 = 편의시설만) -->
          <div class="area-sheet-content">
            <!-- 마트/백화점 -->
            <template v-if="property.facilities?.convenience?.mart?.length || property.facilities?.convenience?.department?.length">
              <div class="area-category">
                <div class="area-category-header">
                  <svg class="area-cat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                  <span>마트/백화점</span>
                </div>
                <ul class="area-list">
                  <li v-for="item in property.facilities?.convenience?.mart?.slice(0, 5)" :key="'mart-'+item.name">
                    {{ item.name }} <span class="area-dist">{{ formatDistance(item.distance) }}</span>
                  </li>
                  <li v-for="item in property.facilities?.convenience?.department?.slice(0, 3)" :key="'dept-'+item.name">
                    {{ item.name }} <span class="area-dist">{{ formatDistance(item.distance) }}</span>
                  </li>
                </ul>
              </div>
            </template>

            <!-- 종합병원 -->
            <template v-if="property.facilities?.convenience?.hospital?.length">
              <div class="area-category">
                <div class="area-category-header">
                  <svg class="area-cat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                  <span>종합병원</span>
                </div>
                <ul class="area-list">
                  <li v-for="item in property.facilities?.convenience?.hospital?.slice(0, 5)" :key="'hospital-'+item.name">
                    {{ item.name }} <span class="area-dist">{{ formatDistance(item.distance) }}</span>
                  </li>
                </ul>
              </div>
            </template>

            <!-- 공원 -->
            <template v-if="property.facilities?.convenience?.park?.length">
              <div class="area-category">
                <div class="area-category-header">
                  <svg class="area-cat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 3v4M3 5h4M6 17v4m-2-2h4M13 3l2 2m0 0l2 2m-2-2l-2 2m2-2l2-2M3 13h4l2 2m6 6l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"/>
                  </svg>
                  <span>공원</span>
                </div>
                <ul class="area-list">
                  <li v-for="item in property.facilities?.convenience?.park?.slice(0, 5)" :key="'park-'+item.name">
                    {{ item.name }} <span class="area-dist">{{ formatDistance(item.distance) }}</span>
                  </li>
                </ul>
              </div>
            </template>

            <!-- 약국 -->
            <template v-if="property.facilities?.convenience?.pharmacy?.length">
              <div class="area-category">
                <div class="area-category-header">
                  <svg class="area-cat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  <span>약국</span>
                </div>
                <ul class="area-list">
                  <li v-for="item in property.facilities?.convenience?.pharmacy?.slice(0, 5)" :key="'pharmacy-'+item.name">
                    {{ item.name }} <span class="area-dist">{{ formatDistance(item.distance) }}</span>
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </div>
      </div>


      <!-- 여백 -->
      <div class="bottom-space"></div>
    </div>

    <!-- 하단 고정 버튼 -->
    <div v-if="!loading && !error" class="bottom-bar">
      <div class="bottom-price">
        <span class="price-main">{{ property.initialFund || '가격문의' }}</span>
        <span v-if="property.initialFund" class="price-sub">부터</span>
      </div>
      <button class="cta-btn" @click="openConsultantSelect" :disabled="partners.length === 0">
        {{ partners.length ? '상담 신청하기' : '상담사 없음' }}
      </button>
    </div>

    <!-- 상담사 선택 시트 -->
    <ConsultantSelectSheet
      v-if="showConsultantSelect"
      :property="property"
      :partners="partners"
      @close="showConsultantSelect = false"
      @select="openConsultationRequest"
    />

    <!-- 상담 신청 시트 -->
    <ConsultationRequestSheet
      v-if="showConsultationRequest"
      :property="property"
      :partner="selectedPartner"
      :adSource="selectedPartnerAdSource"
      @close="closeConsultationRequest"
      @submit="submitConsultation"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { doc, getDoc, collection, query, where, getDocs, addDoc } from 'firebase/firestore'
import { db, auth } from '@/firebase/firebase'
import { useFavorite } from '@/composables/main/useFavorite'
import { useRecent } from '@/composables/main/useRecent'
import { usePoint } from '@/composables/main/usePoint'
import { useRecommendAd } from '@/composables/partners/useRecommendAd'
import { usePropertyComment } from '@/composables/main/usePropertyComment'
import { useAuth } from '@/composables/common/useAuth'
import ConsultantSelectSheet from '@/components/main/property/ConsultantSelectSheet.vue'
import ConsultationRequestSheet from '@/components/main/property/ConsultationRequestSheet.vue'

export default {
  name: 'PropertyDetailView',
  components: { ConsultantSelectSheet, ConsultationRequestSheet },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { isFavorite, toggleFavorite, loadFavorites } = useFavorite()
    const { addRecent } = useRecent()
    const { rewardConsultation } = usePoint()
    const { getRandomAd, incrementClick, incrementImpression } = useRecommendAd()
    const { user, isLoggedIn } = useAuth()
    const {
      getComments,
      getReplies,
      createComment,
      deleteComment,
      getCommentCount
    } = usePropertyComment()

    const loading = ref(true)
    const error = ref(null)
    const property = ref({})
    const partners = ref([])
    const jumpSlots = ref([])
    const isFavorited = ref(false)
    const mapContainer = ref(null)
    const expandedType = ref(null)
    const selectedTypeIdx = ref(0)

    // 추천 섹션 상태
    const recommendAdBanner = ref(null)
    const similarProperties = ref([])

    // 댓글 섹션 상태
    const comments = ref([])
    const commentCount = ref(0)
    const newComment = ref('')
    const replyingTo = ref(null)  // 답글 대상 댓글 ID
    const replyContent = ref('')
    const expandedReplies = ref({})  // { commentId: [replies] }
    const loadingReplies = ref({})   // { commentId: boolean }
    const commentLoading = ref(false)

    const showConsultantSelect = ref(false)
    const showConsultationRequest = ref(false)
    const selectedPartner = ref(null)

    // 시설 모달 상태 (사용안함 - 호환성 유지)
    const showFacilityModal = ref(false)
    const facilityModalType = ref('convenience')

    // 시설 상세 페이지 상태
    const showFacilityDetail = ref(false)
    const facilityDetailType = ref('convenience')
    const facilityMapContainer = ref(null)

    // 어린이집/유치원 탭 상태
    const preschoolTab = ref('daycare')
    // 초중고 탭 상태
    const schoolTab = ref('elementary')

    const themeLabels = {
      money: '실투자 3천만', brand: '1군 브랜드', subway: '초역세권',
      safe: '안심보장제', school: '명문학세권', forest: '힐링숲세권',
      job: '직주근접', mall: '몰세권'
    }

    // 새 라벨 정의 (단순화)
    const convenienceLabels = {
      mart: '마트',
      department: '백화점',
      hospital: '종합병원',
      park: '공원',
      pharmacy: '약국'
    }
    const transportLabels = {
      subway: '지하철역',
      rail: '고속철도/일반철도',
      bus: '버스정류장'
    }
    const educationLabels = {
      daycare: '어린이집',
      kindergarten: '유치원',
      elementary: '초등학교',
      middle: '중학교',
      high: '고등학교'
    }

    // 라벨 가져오기 함수
    const getConvenienceLabel = (key) => convenienceLabels[key] || key
    const getTransportLabel = (key) => transportLabels[key] || key
    const getEducationLabel = (key) => educationLabels[key] || key

    // 시설 유형별 SVG 아이콘 (path 데이터)
    const facilityIcons = {
      // 편의시설
      mart: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z', // 쇼핑카트
      department: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', // 빌딩
      hospital: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', // 병원 (십자가 표시용)
      park: 'M5 3v4M3 5h4M6 17v4m-2-2h4M21 12a9 9 0 11-18 0 9 9 0 0118 0z', // 나무/공원
      pharmacy: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z', // 처방전
      // 대중교통
      subway: 'M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z', // 지하철
      rail: 'M13 10V3L4 14h7v7l9-11h-7z', // 고속철도/일반철도 (번개)
      bus: 'M8 7h8M8 11h8m-8 4h4m-7 5h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2zm2-2v2m8-2v2', // 버스
      // 학군
      daycare: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', // 아이들
      kindergarten: 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', // 스마일
      elementary: 'M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z', // 학교모자
      middle: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', // 책
      high: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' // 클립보드 체크
    }

    // 시설 유형 가져오기 (이름으로 유형 판별)
    const getFacilityType = (item, category) => {
      if (category === 'convenience') {
        const c = property.value.facilities?.convenience
        if (c) {
          for (const [key, items] of Object.entries(c)) {
            if (Array.isArray(items) && items.some(i => i.name === item.name && i.distance === item.distance)) {
              return key
            }
          }
        }
      } else if (category === 'transport') {
        const t = property.value.facilities?.transport
        if (t) {
          for (const [key, items] of Object.entries(t)) {
            if (Array.isArray(items) && items.some(i => i.name === item.name && i.distance === item.distance)) {
              return key
            }
          }
        }
      } else if (category === 'education') {
        const e = property.value.facilities?.education
        if (e) {
          for (const [key, items] of Object.entries(e)) {
            if (Array.isArray(items) && items.some(i => i.name === item.name && i.distance === item.distance)) {
              return key
            }
          }
        }
      }
      return null
    }

    // 아이콘 path 가져오기
    const getIconPath = (type) => facilityIcons[type] || ''

    // 도보 시간 계산 (평균 도보 속도 4km/h = 약 67m/분)
    const getWalkTime = (distance) => {
      const minutes = Math.round(distance / 67)
      return minutes < 1 ? '1분' : `${minutes}분`
    }

    const getKoreanTime = () => {
      const now = new Date()
      const kt = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }))
      return {
        date: `${kt.getFullYear()}-${String(kt.getMonth()+1).padStart(2,'0')}-${String(kt.getDate()).padStart(2,'0')}`,
        time: `${String(kt.getHours()).padStart(2,'0')}:${String(kt.getMinutes()).padStart(2,'0')}`
      }
    }

    const jumpingPartners = computed(() => {
      const { date, time } = getKoreanTime()
      const ids = new Set()
      for (const slot of jumpSlots.value) {
        if (slot.startDate <= date && date <= slot.endDate) {
          if (slot.timeSlots?.some(ts => time >= ts.start && time < ts.end)) {
            ids.add(slot.partnerId)
          }
        }
      }
      return partners.value.filter(p => ids.has(p.partnerId))
    })

    const normalPartners = computed(() => {
      const jumpIds = new Set(jumpingPartners.value.map(p => p.partnerId))
      return partners.value.filter(p => !jumpIds.has(p.partnerId))
    })

    // 시설 존재 여부 computed
    const hasConvenienceFacilities = computed(() => {
      const c = property.value.facilities?.convenience
      if (!c) return false
      return Object.values(c).some(arr => Array.isArray(arr) && arr.length > 0)
    })
    const hasTransportFacilities = computed(() => {
      const t = property.value.facilities?.transport
      if (!t) return false
      return Object.values(t).some(arr => Array.isArray(arr) && arr.length > 0)
    })
    const hasEducationFacilities = computed(() => {
      const e = property.value.facilities?.education
      if (!e) return false
      return Object.values(e).some(arr => Array.isArray(arr) && arr.length > 0)
    })
    // 어린이집/유치원 있는지
    const hasPreschoolFacilities = computed(() => {
      const e = property.value.facilities?.education
      if (!e) return false
      return (e.daycare?.length > 0) || (e.kindergarten?.length > 0)
    })
    // 초중고 있는지
    const hasSchoolFacilities = computed(() => {
      const e = property.value.facilities?.education
      if (!e) return false
      return (e.elementary?.length > 0) || (e.middle?.length > 0) || (e.high?.length > 0)
    })

    // 도보 시간 계산 (분 단위, 평균 도보 속도 80m/분)
    const getWalkMinutes = (distance) => {
      const minutes = Math.round(distance / 80)
      return minutes < 1 ? 1 : minutes
    }

    // 도보 시간에 따른 CSS 클래스
    const getWalkClass = (distance) => {
      const minutes = getWalkMinutes(distance)
      if (minutes <= 5) return 'walk-5'
      if (minutes <= 10) return 'walk-10'
      if (minutes <= 15) return 'walk-15'
      if (minutes <= 20) return 'walk-20'
      return 'walk-over'
    }

    // 타입 기반 가격 범위 계산
    const priceRange = computed(() => {
      const types = property.value.types
      if (!types || !types.length) return null

      const prices = types.map(t => t.price).filter(p => p && p > 0)
      if (!prices.length) return null

      const min = Math.min(...prices)
      const max = Math.max(...prices)

      if (min === max) {
        return formatPrice(min)
      }
      return `${formatPrice(min)} ~ ${formatPrice(max)}`
    })

    const getStatusLabel = (s) => ({ upcoming: '분양예정', ongoing: '분양중', completed: '분양완료' }[s] || '-')
    const getPropertyTypeLabel = (t) => ({ apartment: '아파트', officetel: '오피스텔', villa: '빌라', commercial: '상가' }[t] || '-')
    const getThemeLabel = (t) => themeLabels[t] || t
    const getHeatingLabel = (t) => ({ individual: '개별난방', central: '중앙난방', district: '지역난방' }[t] || '-')
    const getGasLabel = (t) => ({ city: '도시가스', individual: '개별난방', lpg: 'LPG' }[t] || '-')

    const formatPrice = (p) => {
      if (!p) return '-'
      if (p >= 10000) {
        const uk = Math.floor(p / 10000)
        const man = p % 10000
        return man ? `${uk}억 ${man.toLocaleString()}만` : `${uk}억`
      }
      return `${p.toLocaleString()}만원`
    }
    const formatMoveInDate = (d) => {
      if (!d) return '-'
      const [y, m] = d.split('-')
      return `${y}년 ${parseInt(m)}월`
    }
    const formatDistance = (m) => m < 1000 ? `${m}m` : `${(m/1000).toFixed(1)}km`
    const toggleType = (i) => { expandedType.value = expandedType.value === i ? null : i }

    // 지하철 노선 색상
    const subwayLineColors = {
      '1': '#0052A4', '2': '#00A84D', '3': '#EF7C1C', '4': '#00A5DE',
      '5': '#996CAC', '6': '#CD7C2F', '7': '#747F00', '8': '#E6186C',
      '9': '#BDB092', '수인분당': '#FABE00', '신분당': '#D31145',
      '경의중앙': '#77C4A3', '공항': '#0090D2', '경춘': '#0C8E72',
      '경강': '#003DA5', '서해': '#8FC31F', '인천1': '#7CA8D5',
      '인천2': '#ED8B00', '신림': '#6789CA', 'GTX-A': '#9A6292',
      '우이': '#B7C452', '김포': '#AD8605', '에버': '#509F22',
      '의정부': '#FDA600', '용인': '#55B555'
    }

    // 지하철역 이름에서 노선 추출
    const getSubwayLineNumber = (name) => {
      // 서울 1~9호선
      const lineMatch = name.match(/(\d)호선/)
      if (lineMatch) return lineMatch[1]
      // 수인분당선
      if (name.includes('수인분당') || name.includes('분당')) return '수인분당'
      // 신분당선
      if (name.includes('신분당')) return '신분당'
      // 경의중앙선
      if (name.includes('경의중앙') || name.includes('경의') || name.includes('중앙')) return '경의중앙'
      // 공항철도
      if (name.includes('공항')) return '공항'
      // 경춘선
      if (name.includes('경춘')) return '경춘'
      // 경강선
      if (name.includes('경강')) return '경강'
      // 서해선
      if (name.includes('서해')) return '서해'
      // 인천 1호선
      if (name.includes('인천1') || name.includes('인천 1')) return '인천1'
      // 인천 2호선
      if (name.includes('인천2') || name.includes('인천 2')) return '인천2'
      // 신림선
      if (name.includes('신림')) return '신림'
      // GTX
      if (name.includes('GTX')) return 'GTX-A'
      // 우이신설선
      if (name.includes('우이')) return '우이'
      // 김포골드라인
      if (name.includes('김포')) return '김포'
      // 에버라인
      if (name.includes('에버')) return '에버'
      // 의정부경전철
      if (name.includes('의정부')) return '의정부'
      // 용인경전철
      if (name.includes('용인')) return '용인'
      // 역 이름에서 호선 정보 추출 시도
      return ''
    }

    // 노선 색상 가져오기
    const getSubwayLineColor = (name) => {
      const line = getSubwayLineNumber(name)
      return subwayLineColors[line] || '#64748b'
    }

    // 시설 개수 계산
    const getConvenienceCount = computed(() => {
      const c = property.value.facilities?.convenience
      if (!c) return 0
      return Object.values(c).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)
    })
    const getTransportCount = computed(() => {
      const t = property.value.facilities?.transport
      if (!t) return 0
      return Object.values(t).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)
    })
    const getEducationCount = computed(() => {
      const e = property.value.facilities?.education
      if (!e) return 0
      return Object.values(e).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)
    })

    // 모달 제목 (사용안함 - 호환성)
    const getFacilityModalTitle = computed(() => {
      const titles = {
        convenience: '주변 편의시설',
        transport: '주변 대중교통',
        education: '주변 학군'
      }
      return titles[facilityModalType.value] || ''
    })

    // 모달 열기/닫기 (사용안함 - 호환성)
    const openFacilityModal = (type) => {
      facilityModalType.value = type
      showFacilityModal.value = true
      document.body.style.overflow = 'hidden'
    }
    const closeFacilityModal = () => {
      showFacilityModal.value = false
      document.body.style.overflow = ''
    }

    // 미리보기 5개 (거리순으로 정렬 후 상위 5개)
    const getConveniencePreview = computed(() => {
      const c = property.value.facilities?.convenience
      if (!c) return []
      const all = []
      Object.values(c).forEach(arr => {
        if (Array.isArray(arr)) all.push(...arr)
      })
      return all.sort((a, b) => a.distance - b.distance).slice(0, 5)
    })
    const getTransportPreview = computed(() => {
      const t = property.value.facilities?.transport
      if (!t) return []
      const all = []
      Object.values(t).forEach(arr => {
        if (Array.isArray(arr)) all.push(...arr)
      })
      return all.sort((a, b) => a.distance - b.distance).slice(0, 5)
    })
    const getEducationPreview = computed(() => {
      const e = property.value.facilities?.education
      if (!e) return []
      const all = []
      Object.values(e).forEach(arr => {
        if (Array.isArray(arr)) all.push(...arr)
      })
      return all.sort((a, b) => a.distance - b.distance).slice(0, 5)
    })

    // 상세 페이지 제목
    const getFacilityDetailTitle = computed(() => {
      const titles = {
        convenience: '주변 편의시설',
        transport: '주변 대중교통',
        education: '주변 학군'
      }
      return titles[facilityDetailType.value] || ''
    })

    // 상세 페이지 열기/닫기
    const openFacilityDetail = async (type) => {
      facilityDetailType.value = type
      showFacilityDetail.value = true
      document.body.style.overflow = 'hidden'
      await nextTick()
      setTimeout(initFacilityMap, 100)
    }
    const closeFacilityDetail = () => {
      showFacilityDetail.value = false
      document.body.style.overflow = ''
    }

    // 상세 페이지 지도 초기화 (3km 원이 보이도록 줌 레벨 조정)
    const initFacilityMap = () => {
      if (!facilityMapContainer.value || !property.value.coordinates) return
      if (!window.kakao?.maps) return

      const create = () => {
        const coords = new window.kakao.maps.LatLng(
          property.value.coordinates.lat,
          property.value.coordinates.lng
        )
        // level 7 = 약 3km 반경이 화면에 잘 보이는 줌 레벨
        const map = new window.kakao.maps.Map(facilityMapContainer.value, {
          center: coords,
          level: 7
        })
        // 현장 마커
        const marker = new window.kakao.maps.Marker({ map, position: coords })
        new window.kakao.maps.InfoWindow({
          content: `<div style="padding:5px 10px;font-size:12px;white-space:nowrap;font-weight:600;">${property.value.name}</div>`
        }).open(map, marker)

        // 3km 반경 원
        new window.kakao.maps.Circle({
          map,
          center: coords,
          radius: 3000,
          strokeWeight: 2,
          strokeColor: '#2563eb',
          strokeOpacity: 0.8,
          fillColor: '#2563eb',
          fillOpacity: 0.1
        })
      }

      window.kakao.maps.LatLng ? create() : window.kakao.maps.load(create)
    }

    const initMap = async () => {
      if (!mapContainer.value || !property.value.address) return
      if (!window.kakao?.maps) return

      const create = () => {
        if (property.value.coordinates) {
          const coords = new window.kakao.maps.LatLng(property.value.coordinates.lat, property.value.coordinates.lng)
          render(coords)
        } else {
          const gc = new window.kakao.maps.services.Geocoder()
          gc.addressSearch(property.value.address, (res, status) => {
            if (status === window.kakao.maps.services.Status.OK) {
              render(new window.kakao.maps.LatLng(res[0].y, res[0].x))
            }
          })
        }
      }

      const render = (coords) => {
        const map = new window.kakao.maps.Map(mapContainer.value, { center: coords, level: 3 })
        const marker = new window.kakao.maps.Marker({ map, position: coords })
        new window.kakao.maps.InfoWindow({
          content: `<div style="padding:5px 10px;font-size:12px;white-space:nowrap;">${property.value.name}</div>`
        }).open(map, marker)
      }

      window.kakao.maps.LatLng ? create() : window.kakao.maps.load(create)
    }

    const loadData = async () => {
      const id = route.params.id
      try {
        loading.value = true
        const snap = await getDoc(doc(db, 'properties', id))
        if (!snap.exists()) { error.value = '현장을 찾을 수 없습니다'; return }
        property.value = { id: snap.id, ...snap.data() }

        const pq = query(collection(db, 'propertyPartners'), where('propertyId', '==', id), where('status', '==', 'active'))
        partners.value = (await getDocs(pq)).docs.map(d => ({ id: d.id, ...d.data() }))

        const jq = query(collection(db, 'jumpSlots'), where('propertyId', '==', id), where('status', '==', 'active'))
        jumpSlots.value = (await getDocs(jq)).docs.map(d => ({ id: d.id, ...d.data() }))

        addRecent(id)
        await loadFavorites()
        isFavorited.value = isFavorite(id)

        // 추천 섹션 로드
        loadRecommendations(id)

        // 댓글 로드
        loadComments(id)
      } catch (e) {
        console.error(e)
        error.value = '데이터를 불러오지 못했습니다'
      } finally {
        loading.value = false
        // loading이 false가 된 후 DOM이 렌더링되면 지도 초기화
        await nextTick()
        setTimeout(initMap, 100)
      }
    }

    // 추천 섹션 로드
    const loadRecommendations = async (currentId) => {
      try {
        // 1. 추천 광고 조회 (현재 시간대)
        const recommendAd = await getRandomAd()
        if (recommendAd) {
          recommendAdBanner.value = recommendAd
          // 노출 수 증가
          incrementImpression(recommendAd.id)
        }

        // 2. 유사 현장 추천 (같은 지역 기반)
        const currentProperty = property.value
        const region = currentProperty.region // 지역
        const maxCount = recommendAd ? 2 : 3 // AD 있으면 2개, 없으면 3개

        // 같은 지역의 다른 현장 조회
        let similarQuery
        if (region) {
          similarQuery = query(
            collection(db, 'properties'),
            where('region', '==', region)
          )
        } else {
          // 지역 없으면 전체에서 랜덤
          similarQuery = query(collection(db, 'properties'))
        }

        const similarSnap = await getDocs(similarQuery)
        const candidates = similarSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.id !== currentId) // 현재 현장 제외

        // 랜덤 셔플 후 maxCount개 선택
        const shuffled = candidates.sort(() => Math.random() - 0.5)
        similarProperties.value = shuffled.slice(0, maxCount)
      } catch (e) {
        console.error('추천 로드 실패:', e)
      }
    }

    // 댓글 로드
    const loadComments = async (propertyId) => {
      try {
        comments.value = await getComments(propertyId)
        commentCount.value = await getCommentCount(propertyId)
      } catch (e) {
        console.error('댓글 로드 실패:', e)
      }
    }

    // 댓글 작성
    const submitComment = async () => {
      if (!newComment.value.trim()) return
      if (!isLoggedIn.value) {
        alert('로그인이 필요합니다.')
        return
      }

      commentLoading.value = true
      try {
        const result = await createComment({
          propertyId: route.params.id,
          userId: user.value.uid,
          userEmail: user.value.email,
          content: newComment.value.trim(),
          parentId: null
        })

        if (result.success) {
          newComment.value = ''
          await loadComments(route.params.id)
        }
      } catch (e) {
        console.error('댓글 작성 실패:', e)
      } finally {
        commentLoading.value = false
      }
    }

    // 답글 작성
    const submitReply = async (parentId) => {
      if (!replyContent.value.trim()) return
      if (!isLoggedIn.value) {
        alert('로그인이 필요합니다.')
        return
      }

      commentLoading.value = true
      try {
        const result = await createComment({
          propertyId: route.params.id,
          userId: user.value.uid,
          userEmail: user.value.email,
          content: replyContent.value.trim(),
          parentId
        })

        if (result.success) {
          replyContent.value = ''
          replyingTo.value = null
          // 해당 댓글의 답글 새로고침
          await loadRepliesForComment(parentId)
          await loadComments(route.params.id)
        }
      } catch (e) {
        console.error('답글 작성 실패:', e)
      } finally {
        commentLoading.value = false
      }
    }

    // 답글 토글 (보기/숨기기)
    const toggleReplies = async (commentId) => {
      if (expandedReplies.value[commentId]) {
        // 이미 열려있으면 닫기
        delete expandedReplies.value[commentId]
      } else {
        // 답글 로드
        await loadRepliesForComment(commentId)
      }
    }

    // 특정 댓글의 답글 로드
    const loadRepliesForComment = async (commentId) => {
      loadingReplies.value[commentId] = true
      try {
        const replies = await getReplies(commentId)
        expandedReplies.value[commentId] = replies
      } catch (e) {
        console.error('답글 로드 실패:', e)
      } finally {
        loadingReplies.value[commentId] = false
      }
    }

    // 답글 입력 열기
    const openReplyInput = (commentId) => {
      replyingTo.value = commentId
      replyContent.value = ''
    }

    // 답글 입력 취소
    const cancelReply = () => {
      replyingTo.value = null
      replyContent.value = ''
    }

    // 댓글 삭제
    const handleDeleteComment = async (commentId, parentId = null) => {
      if (!confirm('댓글을 삭제하시겠습니까?')) return

      try {
        await deleteComment(commentId, parentId)
        if (parentId) {
          await loadRepliesForComment(parentId)
        }
        await loadComments(route.params.id)
      } catch (e) {
        console.error('댓글 삭제 실패:', e)
      }
    }

    // 시간 포맷 (N분 전, N시간 전, N일 전)
    const formatTimeAgo = (dateStr) => {
      const date = new Date(dateStr)
      const now = new Date()
      const diff = Math.floor((now - date) / 1000)

      if (diff < 60) return '방금 전'
      if (diff < 3600) return `${Math.floor(diff / 60)}분 전`
      if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`
      if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`
      return date.toLocaleDateString('ko-KR')
    }

    // AD 클릭 핸들러
    const handleAdClick = (ad) => {
      incrementClick(ad.id)
      // 캠페인 랜딩 페이지로 이동
      if (ad.campaignCode) {
        router.push(`/p/${ad.campaignCode}?from=recommend`)
      }
    }

    const goBack = () => router.back()
    const shareProperty = async () => {
      const url = window.location.href
      if (navigator.share) {
        try { await navigator.share({ title: property.value.name, url }) } catch (e) { console.log('공유 취소', e) }
      } else {
        navigator.clipboard.writeText(url)
        alert('링크 복사됨')
      }
    }
    const handleToggleFavorite = async () => {
      await toggleFavorite(route.params.id)
      isFavorited.value = isFavorite(route.params.id)
    }

    // 선택한 상담사가 점프 중인지 확인하여 adSource 반환
    const selectedPartnerAdSource = computed(() => {
      if (!selectedPartner.value) return null
      const isJumping = jumpingPartners.value.some(
        jp => jp.partnerId === selectedPartner.value.partnerId
      )
      return isJumping ? 'jump' : null
    })

    const selectPartner = (p) => {
      selectedPartner.value = p
      showConsultationRequest.value = true
    }
    const selectRandomPartner = () => {
      if (!partners.value.length) return
      selectedPartner.value = partners.value[Math.floor(Math.random() * partners.value.length)]
      showConsultationRequest.value = true
    }
    const openConsultantSelect = () => { if (partners.value.length) showConsultantSelect.value = true }
    const openConsultationRequest = (p) => {
      selectedPartner.value = p
      showConsultantSelect.value = false
      showConsultationRequest.value = true
    }
    const closeConsultationRequest = () => {
      showConsultationRequest.value = false
      selectedPartner.value = null
    }

    const submitConsultation = async (form) => {
      try {
        const now = new Date()
        const expires = new Date(now.getTime() + 24*60*60*1000)
        const user = auth.currentUser

        // form.fromJump 플래그로 점프 여부 확인 (ConsultationRequestSheet에서 설정됨)
        const isJumpingPartner = form.fromJump || false

        const ref = await addDoc(collection(db, 'consultationRequests'), {
          customerName: form.name,
          customerPhone: form.phone,
          customerId: user?.uid || null,
          customerEmail: user?.email || null,
          propertyId: property.value.id,
          propertyName: property.value.name,
          partnerId: selectedPartner.value.partnerId,
          partnerDisplayName: selectedPartner.value.displayName,
          privacyAgreed: form.privacyAgreed,
          transferAgreed: form.transferAgreed,
          agreedAt: now.toISOString(),
          // 점프 상담사면 banner_pending, 아니면 pending
          status: isJumpingPartner ? 'banner_pending' : 'pending',
          pickedBy: null, pickedByDisplayName: null, pickedAt: null,
          memo: '', consultStatus: null, contactedAt: null,
          fromCampaign: false, campaignId: null, campaignCode: null,
          // 점프 상담사면 fromJump 플래그 설정
          fromJump: isJumpingPartner,
          createdAt: now.toISOString(),
          // 점프 상담사는 24시간 제한 없음 (expiresAt = null)
          expiresAt: isJumpingPartner ? null : expires.toISOString(),
          updatedAt: now.toISOString()
        })

        let msg = ''
        if (user) {
          try {
            const pts = await rewardConsultation(ref.id)
            if (pts > 0) msg = `\n${pts.toLocaleString()}P 적립!`
          } catch (e) { console.error('포인트 적립 실패', e) }
        }
        alert(`상담 신청 완료!${msg}`)
        closeConsultationRequest()
      } catch (e) {
        console.error(e)
        alert('신청 실패')
      }
    }

    // 타입 슬라이더 스와이프 (모바일)
    let typeSwipeStartX = 0
    let typeSwipeIsSwiping = false
    const onTypeSwipeStart = (e) => {
      typeSwipeStartX = e.touches[0].clientX
      typeSwipeIsSwiping = true
    }
    const onTypeSwipeMove = (e) => {
      if (!typeSwipeIsSwiping) return
      // 스와이프 중 스크롤 방지
      e.preventDefault()
    }
    const onTypeSwipeEnd = (e) => {
      if (!typeSwipeIsSwiping) return
      typeSwipeIsSwiping = false
      const diff = typeSwipeStartX - e.changedTouches[0].clientX
      const typesLength = property.value.types?.length || 1
      if (diff > 50 && selectedTypeIdx.value < typesLength - 1) {
        // 왼쪽으로 스와이프 → 다음 슬라이드
        selectedTypeIdx.value++
      } else if (diff < -50 && selectedTypeIdx.value > 0) {
        // 오른쪽으로 스와이프 → 이전 슬라이드
        selectedTypeIdx.value--
      }
    }

    onMounted(loadData)

    return {
      loading, error, property, partners, jumpingPartners, normalPartners,
      isFavorited, mapContainer, expandedType, selectedTypeIdx, priceRange,
      hasConvenienceFacilities, hasTransportFacilities, hasEducationFacilities,
      hasPreschoolFacilities, hasSchoolFacilities, preschoolTab, schoolTab,
      getWalkMinutes, getWalkClass,
      getConvenienceCount, getTransportCount, getEducationCount,
      getConveniencePreview, getTransportPreview, getEducationPreview,
      getStatusLabel, getPropertyTypeLabel, getThemeLabel, getHeatingLabel, getGasLabel,
      getConvenienceLabel, getTransportLabel, getEducationLabel, getWalkTime,
      getFacilityType, getIconPath,
      getSubwayLineNumber, getSubwayLineColor,
      formatPrice, formatMoveInDate, formatDistance, toggleType,
      goBack, shareProperty, handleToggleFavorite,
      selectPartner, selectRandomPartner,
      showConsultantSelect, showConsultationRequest, selectedPartner, selectedPartnerAdSource,
      openConsultantSelect, openConsultationRequest, closeConsultationRequest, submitConsultation,
      // 시설 모달 (사용안함)
      showFacilityModal, facilityModalType, getFacilityModalTitle,
      openFacilityModal, closeFacilityModal,
      // 시설 상세 페이지
      showFacilityDetail, facilityDetailType, facilityMapContainer, getFacilityDetailTitle,
      openFacilityDetail, closeFacilityDetail,
      // 타입 슬라이더 스와이프
      onTypeSwipeStart, onTypeSwipeMove, onTypeSwipeEnd,
      // 추천 섹션
      recommendAdBanner, similarProperties, handleAdClick,
      // 댓글 섹션
      comments, commentCount, newComment, replyingTo, replyContent,
      expandedReplies, loadingReplies, commentLoading, isLoggedIn, user,
      submitComment, submitReply, toggleReplies, openReplyInput, cancelReply,
      handleDeleteComment, formatTimeAgo
    }
  }
}
</script>

<style scoped>
.detail-page {
  min-height: 100vh;
  background: #ffffff;
}

/* 헤더 */
.detail-header {
  position: sticky;
  top: 60px; /* 메인헤더(60px) 바로 아래 붙음 */
  z-index: 40;
  max-width: 480px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 12px;
  background: white;
}

@media (max-width: 768px) {
  .detail-header {
    top: 0; /* 모바일: 최상단 */
  }
}
.header-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
}
.header-title {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 8px;
}
.header-actions {
  display: flex;
  gap: 0;
}

/* 상태 박스 */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding-top: 56px;
  color: #94a3b8;
}
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #2563eb;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* 컨텐츠 */
.content {
  max-width: 480px;
  margin: 0 auto;
}

/* 요약 정보 바 */
.summary-bar {
  background: white;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
}
.summary-address {
  font-size: 13px;
  color: #1e293b;
  margin-bottom: 4px;
  font-weight: 500;
}
.summary-info {
  font-size: 12px;
  color: #475569;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}
.summary-info .divider {
  color: #cbd5e1;
}

/* 이미지 영역 (썸네일) - 비율 16:9 */
.image-area {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
}
.image-area img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.badges {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  gap: 6px;
}
.badge {
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 20px;
}
.badge-upcoming { background: #fef3c7; color: #d97706; }
.badge-ongoing { background: #d1fae5; color: #059669; }
.badge-completed { background: #e2e8f0; color: #64748b; }
.badge-type { background: #dbeafe; color: #2563eb; }

/* 섹션 */
.section {
  background: white;
  padding: 20px 16px;
  margin-bottom: 8px;
}
.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
}

/* 기본 정보 */
.name {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}
.price-range {
  margin: 0;
}
.range-value {
  font-size: 18px;
  font-weight: 700;
  color: #2563eb;
}

/* 가격 섹션 */
.price-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.price-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.price-row .label {
  font-size: 13px;
  color: #64748b;
}
.price-row .value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}
.price-row .value.primary {
  color: #2563eb;
  font-size: 16px;
}

/* 정보 그리드 */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.info-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
}
.info-label {
  font-size: 13px;
  color: #64748b;
}
.info-value {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
}

/* 평형/타입 (탭 형태) */
.type-section {
  background: white;
}
.type-tabs {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  margin-bottom: 16px;
  -webkit-overflow-scrolling: touch;
}
.type-tabs::-webkit-scrollbar {
  display: none;
}
.type-tab {
  flex-shrink: 0;
  padding: 10px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
}
.type-tab:hover {
  background: #f1f5f9;
  color: #1e293b;
}
.type-tab.active {
  background: #0f172a;
  border-color: #0f172a;
  color: white;
}

/* 선택된 타입 상세 */
.type-detail-card {
  background: #f8fafc;
  border-radius: 16px;
  padding: 20px;
}
.type-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}
.type-detail-left {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.type-detail-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}
.type-detail-pyeong {
  font-size: 14px;
  color: #64748b;
}
.type-detail-price {
  font-size: 17px;
  font-weight: 700;
  color: #2563eb;
}
.type-detail-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}
.info-item {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}
.info-label {
  color: #64748b;
}
.info-value {
  color: #1e293b;
  font-weight: 500;
}
/* 평면도 - 비율 4:3 */
.type-floorplan {
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  margin-top: 4px;
  object-fit: cover;
}

/* 모바일 타입 슬라이더 */
.type-slider {
  overflow: hidden;
  position: relative;
}
.type-slider-track {
  display: flex;
  transition: transform 0.3s ease;
}
.type-slide {
  flex: 0 0 100%;
  min-width: 100%;
  padding: 0 4px;
  box-sizing: border-box;
}
.type-slide-card {
  background: #f8fafc;
  border-radius: 16px;
  padding: 20px;
}
.type-slide-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e2e8f0;
}
.type-slide-left {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.type-slide-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}
.type-slide-pyeong {
  font-size: 14px;
  color: #64748b;
}
.type-slide-price {
  font-size: 17px;
  font-weight: 700;
  color: #2563eb;
}
.type-slide-info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}
.slide-info-item {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}
.slide-info-label {
  color: #64748b;
}
.slide-info-value {
  color: #1e293b;
  font-weight: 500;
}
/* 모바일 평면도 - 비율 4:3 */
.type-slide-floorplan {
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  margin-top: 4px;
  object-fit: cover;
}
.type-slider-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
}
.slider-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cbd5e1;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: background 0.2s;
}
.slider-dot.active {
  background: #2563eb;
  width: 20px;
  border-radius: 4px;
}

/* 반응형: 데스크탑/모바일 전환 */
.desktop-only {
  display: block;
}
.mobile-only {
  display: none;
}
@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
}

/* 시설 */
.facility-group {
  margin-bottom: 20px;
}
.facility-group:last-child {
  margin-bottom: 0;
}
.facility-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.facility-title::before {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.facility-title.green::before { background: #10b981; }
.facility-title.blue::before { background: #3b82f6; }
.facility-title.purple::before { background: #8b5cf6; }
.facility-cat {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
}
.facility-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  font-size: 12px;
  border-radius: 20px;
}
.chip small {
  font-size: 10px;
  opacity: 0.7;
}
.chip.green { background: #d1fae5; color: #047857; }
.chip.blue { background: #dbeafe; color: #1d4ed8; }
.chip.purple { background: #ede9fe; color: #6d28d9; }

/* 섹션 헤더 (현장명 + 부제목) */
.section-header-fancy {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}
.section-title-fancy {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}
.section-subtitle {
  font-size: 13px;
  color: #2563eb;
  font-weight: 500;
}

/* 카테고리 헤더 */
.facility-cat-header {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 10px;
  margin-top: 16px;
}
.facility-cat-header:first-child {
  margin-top: 0;
}

/* 시설 리스트 공통 */
.facility-list {
  display: flex;
  flex-direction: column;
}
.facility-group {
  margin-bottom: 16px;
}
.facility-group:last-child {
  margin-bottom: 0;
}
.facility-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.facility-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: #d1fae5;
  border-radius: 10px;
}
.facility-item.transport {
  background: #dbeafe;
}
.facility-item.education {
  background: #ede9fe;
}
.facility-name {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}
.facility-distance {
  font-size: 13px;
  color: #64748b;
  text-align: right;
}
.facility-distance small {
  display: block;
  font-size: 11px;
  color: #94a3b8;
}

/* 생활시설 그리드 - 삭제된 부분 대체 */
.lifestyle-label {
  font-size: 13px;
  color: #78350f;
}
.lifestyle-count {
  font-size: 14px;
  font-weight: 700;
  color: #d97706;
}

/* 지도 */
.map-box {
  width: 100%;
  height: 180px;
  background: #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}
.map-addr {
  font-size: 12px;
  color: #64748b;
  margin-top: 10px;
}

/* 상담사 */
.empty-box {
  padding: 40px 0;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}
.partner-group {
  margin-bottom: 16px;
}
.partner-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.jump-tag {
  padding: 2px 6px;
  background: #7c3aed;
  color: white;
  font-size: 10px;
  font-weight: 700;
  border-radius: 4px;
}
.partner-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 8px;
  cursor: pointer;
}
.partner-card.jumping {
  background: #f5f3ff;
}
.avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #3b82f6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
}
.avatar.jumping {
  background: #7c3aed;
}
.partner-info {
  flex: 1;
  min-width: 0;
}
.partner-name {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}
.partner-intro {
  display: block;
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.consult-chip {
  padding: 6px 14px;
  background: #2563eb;
  color: white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 20px;
}
.consult-chip.jumping {
  background: #7c3aed;
}
.quick-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  border: 2px dashed #fbbf24;
  border-radius: 12px;
  color: #d97706;
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  margin-top: 8px;
}

/* 하단 여백 (모바일: 상담버튼 + BottomNav 높이 고려) */
.bottom-space {
  height: 100px;
}
@media (min-width: 769px) {
  .bottom-space {
    height: 80px; /* 웹: 하단 바 높이만큼 여백 */
  }
}

/* 하단 바 - 모바일: 플로팅, 웹: 콘텐츠 내 고정 */
.bottom-bar {
  position: fixed;
  bottom: 64px; /* BottomNav 높이 */
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 480px;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: white;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.08);
}
/* 웹 버전: 하단 고정, 너비 제한, 둥근 모서리 */
@media (min-width: 769px) {
  .bottom-bar {
    bottom: 0;
    max-width: 600px;
    border-radius: 20px 20px 0 0;
  }
}
.bottom-price {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
.price-main {
  font-size: 20px;
  font-weight: 800;
  color: #1e293b;
}
.price-sub {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}
.cta-btn {
  padding: 14px 32px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  font-size: 15px;
  font-weight: 700;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
  transition: all 0.2s ease;
}
.cta-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
}
.cta-btn:disabled {
  background: #cbd5e1;
  box-shadow: none;
}

/* 시설 요약 그리드 */
.facility-summary-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.facility-summary-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: white;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s;
}
.facility-summary-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}
.facility-summary-card.green:hover {
  border-color: #10b981;
  background: #f0fdf4;
}
.facility-summary-card.blue:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}
.facility-summary-card.purple:hover {
  border-color: #8b5cf6;
  background: #f5f3ff;
}
.facility-summary-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.facility-summary-icon.green {
  background: #d1fae5;
  color: #059669;
}
.facility-summary-icon.blue {
  background: #dbeafe;
  color: #2563eb;
}
.facility-summary-icon.purple {
  background: #ede9fe;
  color: #7c3aed;
}
.facility-summary-content {
  flex: 1;
  min-width: 0;
}
.facility-summary-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
}
.facility-summary-desc {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 4px;
}
.facility-summary-count {
  font-size: 13px;
  font-weight: 600;
  color: #2563eb;
}

/* 시설 모달 */
.facility-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.facility-modal {
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  background: white;
  border-radius: 24px 24px 0 0;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
}
@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}
.facility-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}
.facility-modal-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.facility-modal-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.facility-modal-icon.convenience {
  background: #d1fae5;
  color: #059669;
}
.facility-modal-icon.transport {
  background: #dbeafe;
  color: #2563eb;
}
.facility-modal-icon.education {
  background: #ede9fe;
  color: #7c3aed;
}
.facility-modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}
.facility-modal-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}
.facility-modal-close {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  border-radius: 10px;
  transition: all 0.2s;
}
.facility-modal-close:hover {
  background: #f1f5f9;
  color: #475569;
}
.facility-modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}
.facility-modal-group {
  margin-bottom: 24px;
}
.facility-modal-group:last-child {
  margin-bottom: 0;
}
.facility-modal-cat {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.facility-cat-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.facility-cat-icon.green {
  background: #d1fae5;
  color: #059669;
}
.facility-cat-icon.blue {
  background: #dbeafe;
  color: #2563eb;
}
.facility-cat-icon.purple {
  background: #ede9fe;
  color: #7c3aed;
}
.facility-cat-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}
.facility-cat-count {
  font-size: 12px;
  color: #94a3b8;
  margin-left: auto;
}
.facility-modal-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.facility-modal-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-radius: 12px;
}
.facility-modal-item.green {
  background: #f0fdf4;
}
.facility-modal-item.blue {
  background: #eff6ff;
}
.facility-modal-item.purple {
  background: #faf5ff;
}
.facility-item-name {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 12px;
}
.facility-item-meta {
  text-align: right;
  flex-shrink: 0;
}
.facility-item-distance {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  display: block;
}
.facility-item-walk {
  font-size: 11px;
  color: #94a3b8;
}

/* 섹션 헤더 (아이콘 + 제목 + 자세히보기) */
.section-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.section-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.section-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.section-icon.green {
  background: #d1fae5;
  color: #059669;
}
.section-icon.blue {
  background: #dbeafe;
  color: #2563eb;
}
.section-icon.purple {
  background: #ede9fe;
  color: #7c3aed;
}
.section-title-sm {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}
.section-subtitle-sm {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}
.more-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #2563eb;
  background: #eff6ff;
  border-radius: 20px;
  transition: all 0.2s;
}
.more-btn:hover {
  background: #dbeafe;
}

/* 미리보기 리스트 */
.preview-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.preview-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-radius: 12px;
}
.preview-item.green {
  background: #f0fdf4;
}
.preview-item.blue {
  background: #eff6ff;
}
.preview-item.purple {
  background: #faf5ff;
}
.preview-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.preview-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.preview-icon.green {
  background: #d1fae5;
  color: #059669;
}
.preview-icon.blue {
  background: #dbeafe;
  color: #2563eb;
}
.preview-icon.purple {
  background: #ede9fe;
  color: #7c3aed;
}
.preview-name {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.preview-distance {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
}

/* 시설 상세 페이지 (풀스크린) */
.facility-detail-page {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.facility-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 12px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}
.facility-detail-map {
  width: 100%;
  height: 200px;
  background: #e2e8f0;
  flex-shrink: 0;
}
.facility-detail-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}
.facility-detail-info {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}
.facility-detail-group {
  margin-bottom: 24px;
}
.facility-detail-group:last-child {
  margin-bottom: 0;
}
.facility-detail-cat {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
}
.cat-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.cat-icon.green {
  background: #d1fae5;
  color: #059669;
}
.cat-icon.blue {
  background: #dbeafe;
  color: #2563eb;
}
.cat-icon.purple {
  background: #ede9fe;
  color: #7c3aed;
}
.cat-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}
.cat-count {
  font-size: 12px;
  color: #94a3b8;
  margin-left: auto;
}
.facility-detail-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.facility-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f8fafc;
}
.facility-detail-item:last-child {
  border-bottom: none;
}
.item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.item-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.item-icon.green {
  background: #d1fae5;
  color: #059669;
}
.item-icon.blue {
  background: #dbeafe;
  color: #2563eb;
}
.item-icon.purple {
  background: #ede9fe;
  color: #7c3aed;
}
.item-name {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.item-right {
  text-align: right;
  flex-shrink: 0;
  margin-left: 12px;
}
.item-distance {
  font-size: 14px;
  font-weight: 600;
  color: #2563eb;
  display: block;
}
.item-walk {
  font-size: 11px;
  color: #94a3b8;
}

/* 편의시설 섹션 (모던 스타일) */
.convenience-section {
  background: white;
}
.convenience-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.convenience-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}
.convenience-more {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
}
.convenience-more:hover {
  color: #1e293b;
}
.convenience-list {
  display: flex;
  flex-direction: column;
}
.convenience-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-bottom: 1px solid #f1f5f9;
}
.convenience-item:last-child {
  border-bottom: none;
}
.convenience-item-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.convenience-icon {
  width: 20px;
  height: 20px;
  color: #1e293b;
  flex-shrink: 0;
}
.convenience-name {
  font-size: 15px;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.convenience-distance {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  flex-shrink: 0;
}

/* 편의시설 상세 페이지 스타일 */
.conv-detail-group {
  margin-bottom: 28px;
}
.conv-detail-group:last-child {
  margin-bottom: 0;
}
.conv-detail-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e2e8f0;
}
.conv-detail-icon {
  width: 20px;
  height: 20px;
  color: #1e293b;
  flex-shrink: 0;
}
.conv-detail-label {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
}
.conv-detail-count {
  margin-left: auto;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 10px;
}
.conv-detail-list {
  display: flex;
  flex-direction: column;
}
.conv-detail-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f8fafc;
}
.conv-detail-item:last-child {
  border-bottom: none;
}
.conv-detail-name {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 12px;
}
.conv-detail-meta {
  text-align: right;
  flex-shrink: 0;
}
.conv-detail-dist {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  display: block;
}
.conv-detail-walk {
  font-size: 11px;
  color: #94a3b8;
}

/* 주변 상권 자세히보기 버튼 */
.detail-view-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  margin-top: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}
.detail-view-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
.detail-view-btn svg {
  color: #64748b;
}

/* 상권 바텀시트 오버레이 */
.area-sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

/* 상권 바텀시트 */
.area-sheet {
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  background: white;
  border-radius: 20px 20px 0 0;
  display: flex;
  flex-direction: column;
  animation: sheetSlideUp 0.3s ease-out;
}
@keyframes sheetSlideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

/* 핸들 바 */
.area-sheet-handle {
  display: flex;
  justify-content: center;
  padding: 12px 0 8px;
}
.handle-bar {
  width: 40px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
}

/* 헤더 */
.area-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px 16px;
  border-bottom: 1px solid #f1f5f9;
}
.area-sheet-title {
  display: flex;
  flex-direction: column;
}
.area-property-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}
.area-title-sub {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}
.area-close-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  border-radius: 10px;
  background: none;
  border: none;
  cursor: pointer;
}
.area-close-btn:hover {
  background: #f1f5f9;
  color: #475569;
}

/* 지도 영역 */
.area-sheet-map {
  width: 100%;
  height: 200px;
  background: #e2e8f0;
  flex-shrink: 0;
}

/* 컨텐츠 영역 */
.area-sheet-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* 섹션 (주변 편의시설, 주변 대중교통, 주변 학군) */
.area-section {
  margin-bottom: 28px;
}
.area-section:last-child {
  margin-bottom: 0;
}
.area-section-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 2px solid #1e293b;
}

/* 카테고리 (마트/백화점, 종합병원 등) */
.area-category {
  margin-bottom: 20px;
}
.area-category:last-child {
  margin-bottom: 0;
}
.area-category-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}
.area-cat-icon {
  width: 20px;
  height: 20px;
  color: #1e293b;
  flex-shrink: 0;
}

/* 리스트 */
.area-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.area-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  border-bottom: 1px solid #f8fafc;
}
.area-list li:last-child {
  border-bottom: none;
}
.area-dist {
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
}

/* 대중교통 섹션 */
.transport-section {
  background: white;
}
.transport-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 20px;
}
.transport-category {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}
.transport-category:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}
.transport-cat-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
}
.transport-cat-icon {
  width: 18px;
  height: 18px;
  color: #64748b;
}

/* 지하철 리스트 */
.subway-list {
  display: flex;
  flex-direction: column;
}
.subway-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}
.subway-item:last-child {
  border-bottom: none;
}
.subway-item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.subway-line-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}
.subway-name {
  font-size: 15px;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.subway-distance {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  flex-shrink: 0;
}

/* 일반 대중교통 리스트 (철도, 버스) */
.transport-list {
  display: flex;
  flex-direction: column;
}
.transport-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}
.transport-item:last-child {
  border-bottom: none;
}
.transport-name {
  font-size: 15px;
  font-weight: 500;
  color: #1e293b;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 12px;
}
.transport-distance {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  flex-shrink: 0;
}

/* 어린이집/유치원 섹션 */
.preschool-section {
  background: white;
}
.preschool-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 16px;
}
.preschool-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}
.preschool-tab {
  flex: 1;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
}
.preschool-tab:hover {
  background: #f1f5f9;
}
.preschool-tab.active {
  color: #0f172a;
  background: white;
  border-color: #0f172a;
}
.preschool-content {
  min-height: 100px;
}
.preschool-list {
  display: flex;
  flex-direction: column;
}
.preschool-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}
.preschool-item:last-child {
  border-bottom: none;
}
.preschool-name {
  font-size: 15px;
  font-weight: 500;
  color: #1e293b;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 12px;
}
.preschool-distance {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  flex-shrink: 0;
}
.preschool-empty {
  font-size: 14px;
  color: #94a3b8;
  text-align: center;
  padding: 24px 0;
}

/* 초중고 학군 섹션 */
.school-section {
  background: white;
}
.school-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 12px;
}
.school-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #64748b;
}
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.legend-dot.walk-5 { background: #22c55e; }
.legend-dot.walk-10 { background: #84cc16; }
.legend-dot.walk-15 { background: #eab308; }
.legend-dot.walk-20 { background: #f97316; }
.legend-dot.walk-over { background: #ef4444; }

.school-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}
.school-tab {
  flex: 1;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
}
.school-tab:hover {
  background: #f1f5f9;
}
.school-tab.active {
  color: #0f172a;
  background: white;
  border-color: #0f172a;
}
.school-content {
  min-height: 100px;
}
.school-list {
  display: flex;
  flex-direction: column;
}
.school-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}
.school-item:last-child {
  border-bottom: none;
}
.school-item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.walk-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}
.walk-badge.walk-5 { background: #22c55e; }
.walk-badge.walk-10 { background: #84cc16; }
.walk-badge.walk-15 { background: #eab308; }
.walk-badge.walk-20 { background: #f97316; }
.walk-badge.walk-over { background: #ef4444; }

.school-name {
  font-size: 15px;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.school-distance {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  flex-shrink: 0;
}
.school-empty {
  font-size: 14px;
  color: #94a3b8;
  text-align: center;
  padding: 24px 0;
}

/* 추천 섹션 */
.recommend-section {
  background: white;
  padding-bottom: 24px;
}
.recommend-cards {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;
  -webkit-overflow-scrolling: touch;
}
.recommend-cards::-webkit-scrollbar {
  display: none;
}
.recommend-card {
  flex: 0 0 140px;
  text-decoration: none;
  display: block;
  position: relative;
}
.recommend-card.ad-card {
  cursor: pointer;
}
.ad-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background: linear-gradient(135deg, #8b5cf6, #6366f1);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  z-index: 1;
}
.recommend-img {
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 12px;
}
.recommend-img-empty {
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
}
.recommend-info {
  margin-top: 8px;
}
.recommend-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.recommend-sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 댓글 섹션 */
.comment-section {
  background: white;
  padding: 20px 16px;
  margin-bottom: 8px;
}
.comment-header {
  margin-bottom: 12px;
}
.comment-header .section-title {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}
.comment-count {
  font-size: 14px;
  font-weight: 600;
  color: #2563eb;
  margin-left: 4px;
}

/* 댓글 입력 */
.comment-input-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 16px;
}
.comment-textarea {
  flex: 1;
  border: none;
  background: transparent;
  resize: none;
  font-size: 13px;
  color: #1e293b;
  outline: none;
  line-height: 1.4;
}
.comment-textarea::placeholder {
  color: #94a3b8;
}
.comment-textarea:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.comment-submit-btn {
  flex-shrink: 0;
  padding: 6px 14px;
  background: #2563eb;
  color: white;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}
.comment-submit-btn:hover:not(:disabled) {
  background: #1d4ed8;
}
.comment-submit-btn:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

/* 댓글 목록 */
.comment-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.no-comments {
  text-align: center;
  padding: 32px 16px;
  color: #94a3b8;
  font-size: 14px;
}
.comment-item {
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 16px;
}
.comment-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.comment-main {
  padding: 0;
}
.comment-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.comment-author {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}
.comment-time {
  font-size: 12px;
  color: #94a3b8;
}
.comment-content {
  font-size: 14px;
  color: #1e293b;
  line-height: 1.5;
  word-break: break-word;
}
.comment-actions {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}
.comment-action-btn {
  background: none;
  border: none;
  font-size: 12px;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
}
.comment-action-btn:hover {
  color: #2563eb;
}
.comment-action-btn.reply-toggle {
  color: #2563eb;
  font-weight: 500;
}
.comment-action-btn.delete {
  color: #94a3b8;
}
.comment-action-btn.delete:hover {
  color: #ef4444;
}

/* 답글 입력 */
.reply-input-box {
  margin-top: 12px;
  margin-left: 20px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 10px;
}
.reply-textarea {
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  font-size: 13px;
  color: #1e293b;
  outline: none;
}
.reply-textarea::placeholder {
  color: #94a3b8;
}
.reply-input-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}
.reply-cancel-btn {
  padding: 6px 12px;
  background: #e2e8f0;
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.reply-cancel-btn:hover {
  background: #cbd5e1;
}
.reply-submit-btn {
  padding: 6px 12px;
  background: #2563eb;
  color: white;
  font-size: 12px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.reply-submit-btn:hover:not(:disabled) {
  background: #1d4ed8;
}
.reply-submit-btn:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

/* 대댓글 목록 */
.reply-list {
  margin-top: 12px;
  margin-left: 20px;
  padding-left: 16px;
  border-left: 2px solid #e2e8f0;
}
.reply-loading {
  font-size: 13px;
  color: #94a3b8;
  padding: 8px 0;
}
.reply-item {
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
}
.reply-item:last-child {
  border-bottom: none;
}
.reply-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.reply-author {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}
.reply-time {
  font-size: 11px;
  color: #94a3b8;
}
.reply-content {
  font-size: 13px;
  color: #334155;
  line-height: 1.4;
  word-break: break-word;
}
.reply-actions {
  margin-top: 4px;
}
</style>
